import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

/** The four admin lists. One vocabulary, so invalidation can't be spelled two ways. */
export type AdminResource = 'customers' | 'devices' | 'drums' | 'users' | 'iot';

/**
 * WHICH CACHES A WRITE INVALIDATES — the part that is easy to get wrong.
 *
 * A naive implementation invalidates only the list you wrote to, and the
 * Customers grid immediately goes stale, because it does not show customers: it
 * shows per-customer COUNTS of users, drums and devices. Add a device and the
 * cached customers list still shows the old device count.
 *
 * The reverse coupling is worse and less obvious. `AdminDevice.customerName` and
 * `AdminDrum.customerName` are DENORMALISED onto those rows, so renaming a
 * customer leaves every cached device and drum displaying the old name. That is
 * why a customer write invalidates everything.
 *
 * Encoded as data rather than as four hand-written invalidate() calls, so it can
 * be asserted directly and cannot drift per component.
 */
export const INVALIDATES: Readonly<Record<AdminResource, readonly AdminResource[]>> = {
  // Counts on the customers grid move whenever any child list changes.
  devices: ['devices', 'customers'],
  drums: ['drums', 'customers'],
  users: ['users', 'customers'],
  // customerName is copied onto device and drum rows; a rename staleness them all.
  customers: ['customers', 'devices', 'drums', 'users'],
  // IoT status is derived from the hub, not from anything the admin writes, so
  // it invalidates only itself. Renaming a customer cannot change whether a
  // device is connected.
  iot: ['iot'],
};

/**
 * Additionally dropped when the ROW SET changes — a create or a delete — and
 * NOT when a row is merely edited.
 *
 * WHY THE DISTINCTION EARNS ITS KEEP. The IoT cache is keyed to the set of
 * serials on the grid: add or delete a device and the cached batch answers for
 * the wrong fleet, so it must go. EDIT a device — which is what setting
 * subscription status is — and the serial set is identical, so dropping it would
 * force an expensive IoT Hub round trip for data that cannot have changed.
 *
 * Subscription status and IoT registration are different columns from different
 * sources: one is admin-written state on the Devices doc, the other is derived
 * from the hub. A subscription edit must not cost a hub query.
 */
export const STRUCTURAL_INVALIDATES: Readonly<Record<AdminResource, readonly AdminResource[]>> = {
  devices: ['iot'],
  drums: [],
  users: [],
  customers: [],
  iot: [],
};

/**
 * Client-side cache for the admin lists, with a manual refresh and
 * write-invalidation.
 *
 * NO TTL, deliberately. A timer would make staleness a question of luck — the
 * operator cannot tell whether what they are looking at is 5 seconds or 5
 * minutes old, and a write that lands just after a refresh would show stale data
 * for the rest of the window. Instead the cache is authoritative until something
 * explicitly changes it: a manual refresh, or a write. That makes staleness a
 * property of actions taken, which the operator can reason about.
 *
 * Session-scoped and in-memory: a page reload starts empty, which is the
 * behaviour people already expect from a refresh.
 */
@Injectable({ providedIn: 'root' })
export class AdminCacheService {
  private readonly store = new Map<AdminResource, unknown>();

  /** For tests and for a "showing cached data" hint if one is ever wanted. */
  has(resource: AdminResource): boolean {
    return this.store.has(resource);
  }

  /**
   * Read through the cache.
   *
   * `force` is the manual refresh: it bypasses the cached value, re-fetches, and
   * REPLACES what is stored — so refresh is a repair, not just a re-render.
   */
  read<T>(resource: AdminResource, fetcher: () => Observable<T>, force = false): Observable<T> {
    if (!force && this.store.has(resource)) {
      return of(this.store.get(resource) as T);
    }
    return fetcher().pipe(tap((value) => this.store.set(resource, value)));
  }

  /**
   * Drop everything a write to `resource` could have made stale.
   *
   * Invalidate rather than patch. Patching the one row you wrote is tempting and
   * wrong here: the server computes counts, denormalises customerName, and
   * stamps subscriptionUpdatedAt/subscriptionSource, so a locally-patched row
   * would differ from what the server actually stored — a cache that looks fresh
   * and is quietly wrong, which is worse than one that refetches.
   */
  invalidate(resource: AdminResource, structural = false): void {
    for (const affected of INVALIDATES[resource]) {
      this.store.delete(affected);
    }
    if (structural) {
      for (const affected of STRUCTURAL_INVALIDATES[resource]) {
        this.store.delete(affected);
      }
    }
  }

  /** Belt and braces for sign-out / tenant switch. */
  clear(): void {
    this.store.clear();
  }
}
